import { MapLayerId, SurveyModuleId, WorkspaceId } from "../../core";
import { internalError } from "../../error";
import { unsafeTag } from "@cartographerio/util";
import { RoleV1 } from "../types.generated";

// All role names and the number of arguments to each:
const roleArities = {
  superuser: 0,
  registered: 1,
  active: 1,
  activeMobile: 1,
  arcgisAdmin: 1,
  userAdmin: 1,
  surveyViewer: 2,
  surveyor: 2,
  surveyDownloader: 2,
  surveyAdmin: 2,
  mapViewer: 2,
  mapDownloader: 2,
};

const widenedRoleArities: { [name: string]: number } = roleArities;

const surveyRoles: RoleNameV1[] = [
  "surveyViewer",
  "surveyor",
  "surveyDownloader",
  "surveyAdmin",
];

const mapLayerRoles: RoleNameV1[] = ["mapViewer", "mapDownloader"];

export type RoleNameV1 = keyof typeof roleArities;

export function optRoleNameV1(role: RoleV1): RoleNameV1 | undefined {
  const [name, ...args] = role.split(":");
  const arity = widenedRoleArities[name];

  if (name == null || arity == null || arity !== args.length) {
    throw internalError(`Bad role: ${role}`);
  } else {
    switch (name) {
      case "superuser":
        return "superuser";
      case "registered":
        return "registered";
      case "active":
        return "active";
      case "activeMobile":
        return "activeMobile";
      case "arcgisAdmin":
        return "arcgisAdmin";
      case "userAdmin":
        return "userAdmin";
      case "surveyViewer":
        return "surveyViewer";
      case "surveyor":
        return "surveyor";
      case "surveyDownloader":
        return "surveyDownloader";
      case "surveyAdmin":
        return "surveyAdmin";
      case "mapViewer":
        return "mapViewer";
      case "mapDownloader":
        return "mapDownloader";
      default:
        return undefined;
    }
  }
}

export function roleNameV1(role: RoleV1): RoleNameV1 {
  const name = optRoleNameV1(role);

  if (name == null) {
    throw internalError(`Bad role: ${role}`);
  } else {
    return name;
  }
}

export function optRoleArityV1(role: RoleV1): number | undefined {
  const name = optRoleNameV1(role);
  const arity = name == null ? undefined : widenedRoleArities[name];
  return arity;
}

export function roleArityV1(role: RoleV1): number {
  const arity = optRoleArityV1(role);

  if (arity == null) {
    throw internalError(`Bad role: ${role}`);
  } else {
    return arity;
  }
}

export function roleArgsV1(role: RoleV1): string[] {
  const [name, ...args] = role.split(":");

  const arity = name == null ? undefined : widenedRoleArities[name];

  if (arity == null || arity !== args.length) {
    throw internalError(`Bad role: ${role}`);
  } else {
    return args;
  }
}

export function roleArgV1(role: RoleV1, index: number): string | undefined {
  const [name, ...args] = role.split(":");

  const arity = name == null ? undefined : widenedRoleArities[name];

  if (arity == null || arity !== args.length) {
    throw internalError(`Bad role: ${role}`);
  } else if (args.length <= index) {
    return undefined;
  } else {
    return args[index];
  }
}

export function roleWorkspaceIdV1(role: RoleV1): WorkspaceId | undefined {
  const str = roleArgV1(role, 0);
  return str == null ? undefined : unsafeTag<"WorkspaceId">(str);
}

export function roleSurveyModuleIdV1(role: RoleV1): SurveyModuleId | undefined {
  if (surveyRoles.includes(roleNameV1(role))) {
    const str = roleArgV1(role, 1);
    return str == null ? undefined : unsafeTag<"SurveyModuleId">(str);
  } else {
    return undefined;
  }
}

export function roleMapLayerIdV1(role: RoleV1): MapLayerId | undefined {
  if (mapLayerRoles.includes(roleNameV1(role))) {
    const str = roleArgV1(role, 1);
    return str == null ? undefined : unsafeTag<"MapLayerId">(str);
  } else {
    return undefined;
  }
}
